import * as THREE from 'three';
import { random } from '../look/noise.js';

/**
 * Drifting motes (ADR-032): ash on the island, dust in the lab's light. A
 * `THREE.Points` cloud moved on the CPU, wrapped inside its volume, never
 * more than 600 to a world.
 */

export const PARTICLES_PER_WORLD = 600;

export type Volume = {
  /** Around the origin: within `radius`, between `low` and `high`, unless `inside` says otherwise. */
  readonly radius: number;
  readonly low: number;
  readonly high: number;
  /** A narrower volume inside that one, e.g. the lab's light shafts. */
  readonly inside?: (p: THREE.Vector3) => boolean;
  /** Where a new one starts, when `inside` is narrow. */
  readonly spawn?: (next: () => number) => THREE.Vector3;
};

export type Drift = {
  readonly points: THREE.Points;
  update(dt: number, elapsed: number): void;
};

export function buildDrift(options: {
  count: number;
  volume: Volume;
  map: THREE.Texture;
  size: number;
  colour: THREE.ColorRepresentation;
  opacity: number;
  /** Metres a second: the wind, and how fast they settle. */
  wind: THREE.Vector3;
  fall: number;
  seed: number;
  name: string;
  additive?: boolean;
}): Drift {
  const count = Math.min(options.count, PARTICLES_PER_WORLD);
  const next = random(options.seed);
  const { volume } = options;
  const place = (p: THREE.Vector3): THREE.Vector3 => {
    if (volume.spawn) return p.copy(volume.spawn(next));
    const a = next() * Math.PI * 2;
    const r = Math.sqrt(next()) * volume.radius;
    return p.set(
      Math.cos(a) * r,
      volume.low + next() * (volume.high - volume.low),
      Math.sin(a) * r,
    );
  };
  const positions = new Float32Array(count * 3);
  const phase = new Float32Array(count);
  const p = new THREE.Vector3();
  for (let i = 0; i < count; i++) {
    place(p);
    positions.set([p.x, p.y, p.z], i * 3);
    phase[i] = next() * 100;
  }
  const geometry = new THREE.BufferGeometry();
  const attribute = new THREE.BufferAttribute(positions, 3).setUsage(THREE.DynamicDrawUsage);
  geometry.setAttribute('position', attribute);
  const material = new THREE.PointsMaterial({
    map: options.map,
    size: options.size,
    sizeAttenuation: true,
    color: options.colour,
    transparent: true,
    opacity: options.opacity,
    depthWrite: false,
    blending: options.additive ? THREE.AdditiveBlending : THREE.NormalBlending,
    fog: true,
  });
  const points = new THREE.Points(geometry, material);
  points.name = options.name;
  points.frustumCulled = false;
  points.raycast = (): void => {};
  return {
    points,
    update(dt) {
      for (let i = 0; i < count; i++) {
        phase[i]! += dt;
        const t = phase[i]!;
        p.fromArray(positions, i * 3);
        p.x += (options.wind.x + Math.sin(t * 0.7 + i) * 0.12) * dt;
        p.y += (options.wind.y - options.fall + Math.sin(t * 0.9 + i * 1.7) * 0.05) * dt;
        p.z += (options.wind.z + Math.cos(t * 0.6 + i * 0.3) * 0.12) * dt;
        const out =
          p.y < volume.low ||
          p.y > volume.high ||
          Math.hypot(p.x, p.z) > volume.radius ||
          (volume.inside !== undefined && !volume.inside(p));
        if (out) {
          place(p);
          // Back in at the top when they settle, so they keep falling; bubbles at the bottom.
          if (!volume.spawn && options.fall > 0) p.y = volume.high;
          if (!volume.spawn && options.fall < 0) p.y = volume.low;
        }
        p.toArray(positions, i * 3);
      }
      attribute.needsUpdate = true;
    },
  };
}
